import { captureException, isExpectedAuthStatus } from '@common/sentry';
import { ErrorTags, SentryTags } from '@common/sentry/SentryTags';
import { NestedData } from '@prophecy/interfaces/generic';
import { ApiErrorKind, attachHttpStatus, ProphecyError } from '@prophecy/utils/error';
import { getTabId } from '@prophecy/utils/session';

import { csrf } from '../../utils/csrf';
import { isNavigatingAway } from '../../utils/pageUnload';
import { replayToken } from '../../utils/replayToken';
import { shareToken } from '../../utils/shareToken';
import { TAB_ID_HEADER } from '../../utils/tabId';

// `path` has ids stripped to keep the tag's cardinality low enough for Sentry to index; `pathname`
// keeps them for the request context, which is not indexed. Query strings are dropped from both —
// they carry share tokens and JWTs, and an issue title has already leaked one.
const describeRequest = (url: URL | RequestInfo) => {
  try {
    const { host, pathname } = new URL((url as Request)?.url ?? url.toString(), window.location.origin);
    return { host, pathname, path: pathname.replace(/\/\d+/g, '/:id') };
  } catch {
    return { host: 'unknown', pathname: 'unknown', path: 'unknown' };
  }
};

export async function fetchURL(
  url: URL | RequestInfo,
  requestConfig: RequestInit | undefined,
  handleUnAuthorizedCall: (status: number, url: string) => boolean = () => true,
  excludeShareTokens?: boolean
) {
  const body = requestConfig?.body;
  const isFormData = body instanceof FormData;
  const requestHeaders: NestedData = {
    accept: 'application/json;',
    'Content-Type': 'application/json;charset=utf-8',
    [csrf.key]: csrf.get(),
    [TAB_ID_HEADER]: getTabId(),
    ...requestConfig?.headers
  };
  const shareTokenValue = shareToken.get?.();
  const replayTokenValue = replayToken.get?.();
  const isApiCall = url.toString().includes('/api/');
  if (shareTokenValue && isApiCall && !excludeShareTokens) {
    (requestHeaders as Record<string, string>)[shareToken.key] = shareTokenValue;
  }
  if (replayTokenValue && isApiCall && !excludeShareTokens) {
    (requestHeaders as Record<string, string>)[replayToken.key] = replayTokenValue;
  }
  if (isFormData && body) {
    delete requestHeaders['Content-Type'];
  }
  const modifiedOptions: RequestInit = {
    credentials: 'include',
    ...requestConfig,
    headers: { [csrf.key]: csrf.get(), ...requestHeaders }
  };

  let response: Response | undefined;

  try {
    response = await fetch(url, modifiedOptions);
  } catch (e) {
    const { host, pathname, path } = describeRequest(url);
    const method = (modifiedOptions.method || 'GET').toUpperCase();

    // A caller cancelled this request. Not a failure to report, and the AbortError has to survive so
    // callers can tell a cancellation from a network error. Deliberately not `signal.aborted`, which
    // is also true for an `AbortSignal.timeout` — those stay on the reported path below.
    if ((e as Error)?.name === 'AbortError') {
      throw e;
    }
    // A fixed message keeps every network failure in one issue: Sentry appends the host to "Failed to
    // fetch" and, with no stack to group on, files one issue per domain. Wording and host go to tags.
    // The wrapper thrown below is report:false, so one failure is not reported twice.
    captureException({
      exception: new ProphecyError('Network request failed', {
        cause: (e as Error)?.name,
        kind: ApiErrorKind.Network
      }),
      errorTags: { [SentryTags.ProphecyErrorType]: ErrorTags.NetworkError },
      captureContext: {
        // A normalised message is not enough on its own: these carry a stack, and Sentry groups on
        // stack frames, so each call site still filed its own issue. The fingerprint is what collapses
        // them; the message is what stops the one issue being named after a single arbitrary host.
        fingerprint: ['network-failure'],
        tags: {
          [SentryTags.PageUnloading]: String(isNavigatingAway()),
          [SentryTags.NetworkErrorName]: (e as Error)?.name,
          [SentryTags.NetworkHost]: host,
          [SentryTags.NetworkMethod]: method,
          [SentryTags.NetworkPath]: path
        },
        // not indexed, so the real ids are affordable here where the tag has to stay groupable
        contexts: { request: { method, url: `${host}${pathname}` } }
      }
    });
    throw new ProphecyError('Something went wrong in an API', { report: false, kind: ApiErrorKind.Network });
  }

  const isAuthorized = handleUnAuthorizedCall(response?.status, url.toString?.());

  if (!response?.ok && !isAuthorized) {
    // the redirect to auth is already latched, so this response is a cancelled request rather than
    // a payload. Returning it lets callers read the error body as data - see METADATA-UI-MQW.
    throw new ProphecyError('Session expired', { cause: response?.status?.toString(), report: false });
  }

  if (!response?.ok && isAuthorized) {
    let errorResponse;
    const clonedResponse = response?.clone();
    try {
      errorResponse = await response?.json();
    } catch {
      // a short plaintext body is a usable 4xx reason; an HTML error page would fill the toast and
      // split Sentry grouping, so those fall back to the status
      const body = (await clonedResponse?.text())?.trim();
      const message =
        body && body.length <= 200 && !body.startsWith('<') ? body : `Something went wrong (${response?.status})`;
      throw new ProphecyError(message, {
        cause: response?.status?.toString(),
        report: !isExpectedAuthStatus(response?.status),
        status: response?.status
      });
    }
    throw attachHttpStatus(errorResponse, response?.status);
  }
  // TODO: remove below logic once backend starts sending right content-type header.
  // `response.ok` guard: forcing application/json onto an HTML error body makes graphql-request throw.
  const headers: [string, string][] = [];
  for (const [key, value] of response.headers.entries()) {
    if (key === 'content-type' && response.ok) {
      headers.push([key, 'application/json']);
    } else {
      headers.push([key, value]);
    }
  }
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers
  });
}
